import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Tile, mixTheme } from '../experience/illustrations';
import Reservation from '../experience/Reservation';
import { PrintSheet } from './MenuAdmin';
import ThemeToggle from '../ThemeToggle';
import Logo from '../Logo';
import { useLanguage } from '../contexts/LanguageContext';
import { LANGS, LANG_LABELS, subscribeAll, menuView, pick, fold } from './model';
import { SearchIcon, CloseIcon } from './icons';
import './menu.css';
import './guest.css';

const UI = {
  en: { book: 'Book a table', kicker: 'Tonight’s menu', search: 'Search the menu', soldOut: 'Sold out', noResults: 'Nothing matches your search.', loading: 'Setting the table…', error: 'The menu could not be loaded.', clear: 'Clear search' },
  fr: { book: 'Réserver', kicker: 'La carte du soir', search: 'Rechercher dans la carte', soldOut: 'Épuisé', noResults: 'Aucun plat ne correspond.', loading: 'On dresse la table…', error: 'La carte n’a pas pu être chargée.', clear: 'Effacer' },
  es: { book: 'Reservar', kicker: 'La carta de esta noche', search: 'Buscar en la carta', soldOut: 'Agotado', noResults: 'No hay resultados.', loading: 'Preparando la mesa…', error: 'No se pudo cargar la carta.', clear: 'Borrar' },
  tr: { book: 'Rezervasyon', kicker: 'Bu akşamın menüsü', search: 'Menüde ara', soldOut: 'Tükendi', noResults: 'Aramanızla eşleşen bir şey yok.', loading: 'Masa hazırlanıyor…', error: 'Menü yüklenemedi.', clear: 'Aramayı temizle' },
};

// A strip of azulejo tiles, as on the home page.
const Frieze = () => (
  <div className="lm-frieze" aria-hidden="true">
    {Array.from({ length: 24 }).map((_, i) => (
      <Tile key={i} theme={mixTheme(i + 100)} />
    ))}
  </div>
);

const GuestMenu = () => {
  const { currentLanguage: lang, changeLanguage } = useLanguage();
  const ui = UI[lang] || UI.en;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(null);
  const chipsRef = useRef(null);

  useEffect(() => subscribeAll(setData, (e) => { console.error(e); setError(e); }), []);

  const config = data?.config || {};
  const hideSoldOut = !!config.hideSoldOut;
  const showSearch = config.showSearch !== false;

  const sections = useMemo(() => {
    if (!data) return [];
    const q = fold(query.trim());
    return menuView(data, config.liveMenuId)
      .map((s) => ({
        ...s,
        dishes: s.dishes.filter((d) => {
          if (hideSoldOut && d.soldOut) return false;
          if (!q) return true;
          return fold(`${pick(d.i18n, lang, 'name')} ${pick(d.i18n, lang, 'description')}`).includes(q);
        }),
      }))
      .filter((s) => s.dishes.length);
  }, [data, config.liveMenuId, hideSoldOut, query, lang]);

  // Scroll-spy: highlight the chip of the section at the top of the viewport.
  useEffect(() => {
    const onScroll = () => {
      let cur = sections[0]?.id || null;
      sections.forEach((s) => {
        const el = document.getElementById(`sec-${s.id}`);
        if (el && el.getBoundingClientRect().top < 200) cur = s.id;
      });
      setActive(cur);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [sections]);

  useEffect(() => {
    // Scroll only the chip row; scrollIntoView would also move the page.
    const row = chipsRef.current;
    const chip = row?.querySelector(`[data-sec="${active}"]`);
    if (chip) row.scrollTo({ left: chip.offsetLeft - row.clientWidth / 2 + chip.offsetWidth / 2, behavior: 'smooth' });
  }, [active]);

  const jump = (id) => document.getElementById(`sec-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const site = config.i18n;

  return (
    <div className="lm lm--guest" lang={lang}>
      <header className="lm-hero">
        <div className="lm-hero-tiles" aria-hidden="true">
          {Array.from({ length: 48 }).map((_, i) => (
            <Tile key={i} theme={mixTheme(i)} />
          ))}
        </div>
        <Link to="/" className="lm-home"><Logo /></Link>
        <a href="#rezervasyon" className="lm-book">{ui.book}</a>
        <div className="lm-arch">
          <p className="lm-arch-kicker">{ui.kicker}</p>
          <h1><Logo className="lm-logo" /></h1>
          {pick(site, lang, 'date') && <div className="lm-date">{pick(site, lang, 'date')}</div>}
          <div className="lm-langs" role="group" aria-label="Language">
            {LANGS.map((l) => (
              <button key={l} type="button" className="lm-lang" aria-pressed={l === lang} onClick={() => changeLanguage(l)}>
                {LANG_LABELS[l]}
              </button>
            ))}
            <ThemeToggle className="lm-lang lm-theme" />
          </div>
        </div>
      </header>

      {error && <div className="lm-status">{ui.error}</div>}
      {!error && !data && <div className="lm-status">{ui.loading}</div>}

      {data && (
        <>
          <div className="lm-tools">
            <div className="lm-tools-in">
              {showSearch && (
                <label className="lm-search">
                  <SearchIcon />
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={ui.search}
                    aria-label={ui.search}
                  />
                  {query && (
                    <button type="button" onClick={() => setQuery('')} aria-label={ui.clear}><CloseIcon /></button>
                  )}
                </label>
              )}
              <nav className="lm-chips" ref={chipsRef}>
                {sections.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    data-sec={s.id}
                    className={`lm-chip${active === s.id ? ' is-active' : ''}`}
                    onClick={() => jump(s.id)}
                  >
                    {pick(s.i18n, lang, 'title')}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          <main className="lm-carta">
            {!sections.length && <div className="lm-empty">{ui.noResults}</div>}
            {!!sections.length && <PrintSheet data={data} lang={lang} sections={sections} fluid />}
          </main>

          <Reservation lang={lang} />

          <footer className="lm-foot">
            {pick(site, lang, 'footer') && <p>{pick(site, lang, 'footer')}</p>}
            <div className="lm-emblem lm-emblem--foot"><Tile theme="oxblood" /></div>
          </footer>
          <Frieze />
        </>
      )}
    </div>
  );
};

export default GuestMenu;
